import React, { useState, useEffect } from 'react';
import {
  HStack,
  Button,
  ButtonGroup,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  VStack,
  Input,
  FormControl,
  FormLabel,
  Box,
  Text,
  Icon,
  useDisclosure,
} from '@chakra-ui/react';
import { CalendarBlank } from 'phosphor-react';

export type PeriodoPreset = 'todos' | 'hoje' | '7d' | '30d' | 'custom';

export interface PeriodoRange {
  preset: PeriodoPreset;
  date_init: string | null;
  date_end: string | null;
}

interface PeriodoFlowFilterProps {
  value: PeriodoRange;
  // eslint-disable-next-line no-unused-vars
  onChange: (range: PeriodoRange) => void;
  // Inclui o preset "Todos" (sem filtro de data) a esquerda. Default false,
  // pra nao alterar o filtro do analiseBots (que sempre tem periodo).
  incluirTodos?: boolean;
}

const PRESETS: Array<{ key: PeriodoPreset; label: string }> = [
  { key: 'hoje', label: 'Hoje' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
];

// Componentes locais (evita o bug de toISOString() converter pra UTC e estourar o dia
// em fusos UTC-3: 23:59:59.999 local vira 02:59:59.999Z do dia seguinte).
const isoDateOnly = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const computePresetRange = (preset: PeriodoPreset): PeriodoRange => {
  if (preset === 'todos') {
    return { preset: 'todos', date_init: null, date_end: null };
  }
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const init = new Date(now);
  if (preset === 'hoje') {
    init.setHours(0, 0, 0, 0);
  } else if (preset === '7d') {
    init.setDate(init.getDate() - 6);
    init.setHours(0, 0, 0, 0);
  } else if (preset === '30d') {
    init.setDate(init.getDate() - 29);
    init.setHours(0, 0, 0, 0);
  }
  return {
    preset,
    date_init: init.toISOString(),
    date_end: end.toISOString(),
  };
};

export const buildDefaultPeriodo = (): PeriodoRange => computePresetRange('7d');

/** Range "sem filtro" — telas cujo default e mostrar tudo (sem data => todos). */
export const buildTodosPeriodo = (): PeriodoRange => ({
  preset: 'todos',
  date_init: null,
  date_end: null,
});

export const PeriodoFlowFilter: React.FC<PeriodoFlowFilterProps> = ({
  value,
  onChange,
  incluirTodos = false,
}) => {
  const [customInit, setCustomInit] = useState(() =>
    value.date_init ? isoDateOnly(new Date(value.date_init)) : '',
  );
  const [customEnd, setCustomEnd] = useState(() =>
    value.date_end ? isoDateOnly(new Date(value.date_end)) : '',
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Sincroniza os inputs do popover com o range ativo. Deps especificas
  // (date_init/date_end/preset) evitam re-render desnecessario E garantem que trocar
  // de preset com mesmo range sincronize os inputs (caso usuario tivesse editado).
  useEffect(() => {
    if (value.date_init && value.date_end) {
      setCustomInit(isoDateOnly(new Date(value.date_init)));
      setCustomEnd(isoDateOnly(new Date(value.date_end)));
    }
  }, [value.date_init, value.date_end, value.preset]);

  const handlePreset = (preset: PeriodoPreset) => {
    onChange(computePresetRange(preset));
  };

  const handleApplyCustom = () => {
    if (!customInit || !customEnd) return;
    const init = new Date(`${customInit}T00:00:00`);
    const end = new Date(`${customEnd}T23:59:59`);
    onChange({
      preset: 'custom',
      date_init: init.toISOString(),
      date_end: end.toISOString(),
    });
    onClose();
  };

  const customLabel = (() => {
    if (value.preset !== 'custom' || !value.date_init || !value.date_end) return 'Customizado';
    const i = new Date(value.date_init);
    const f = new Date(value.date_end);
    const fmt = (d: Date) =>
      `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    return `${fmt(i)} a ${fmt(f)}`;
  })();

  return (
    <HStack spacing={2}>
      <Icon as={CalendarBlank} color="gray.500" />
      <ButtonGroup size="sm" isAttached variant="outline">
        {incluirTodos && (
          <Button
            colorScheme="blue"
            variant={value.preset === 'todos' ? 'solid' : 'outline'}
            onClick={() => handlePreset('todos')}
          >
            Todos
          </Button>
        )}
        {PRESETS.map(p => (
          <Button
            key={p.key}
            colorScheme="blue"
            variant={value.preset === p.key ? 'solid' : 'outline'}
            onClick={() => handlePreset(p.key)}
          >
            {p.label}
          </Button>
        ))}
        <Popover placement="bottom-end" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
          <PopoverTrigger>
            <Button colorScheme="blue" variant={value.preset === 'custom' ? 'solid' : 'outline'}>
              {customLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent w="280px">
            <PopoverArrow />
            <PopoverBody>
              <VStack spacing={3} align="stretch">
                <Text fontSize="xs" color="gray.500" fontWeight="600">
                  Selecione o período
                </Text>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>
                    De
                  </FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    value={customInit}
                    onChange={e => setCustomInit(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>
                    Até
                  </FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    value={customEnd}
                    onChange={e => setCustomEnd(e.target.value)}
                  />
                </FormControl>
                <Box>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    w="100%"
                    onClick={handleApplyCustom}
                    isDisabled={!customInit || !customEnd || customEnd < customInit}
                  >
                    Aplicar
                  </Button>
                </Box>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </ButtonGroup>
    </HStack>
  );
};
